import { PropertySharingFieldPart } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { usePropertyLinkExamplePreview } from '../../../hooks/use-property-link-example-preview';
import {
  propertyLinkCategoryOptions,
  propertyLinkParameterKey,
} from '../../../utils/property-link-template.util';

import { PropertyLinkEditorStep } from './property-link-editor-step';
import type { PropertyLinkExamplePreviewProps } from '../../../types/property-links.types';

/** Display backend-rendered fictional examples without a property lookup or client-side URL calculation. */
export function PropertyLinkExamplePreview({
  site,
}: PropertyLinkExamplePreviewProps) {
  const { t } = useI18n();
  const preview = usePropertyLinkExamplePreview(site);
  if (!preview.available) return null;
  return (
    <PropertyLinkEditorStep
      step={3}
      title={t('propertyLinks.examplePreview')}
      description={t('propertyLinks.examplePreviewHelp')}
    >
      <div aria-live="polite">
        {preview.loading && (
          <Text size="small">{t('propertyLinks.loading')}</Text>
        )}
        {preview.error && (
          <Alert
            variant="danger"
            message={
              <div className="space-y-2">
                <Text>{t('propertyLinks.previewError')}</Text>
                <Button appearance="outline" onClick={preview.retry}>
                  {t('propertyLinks.retry')}
                </Button>
              </div>
            }
          />
        )}
        {preview.candidate &&
          (preview.candidate.url ? (
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
              <Text
                disableAutoLink
                className="break-all font-sans"
                size="small"
              >
                {preview.candidate.url}
              </Text>
            </div>
          ) : (
            <Alert
              variant="warning"
              message={t(
                preview.candidate.reason
                  ? `propertyLinks.reason.${preview.candidate.reason}`
                  : 'propertyLinks.noLink'
              )}
            />
          ))}
      </div>
      <div className="space-y-3">
        <Text size="small" weight="medium">
          {t('propertyLinks.exampleValues')}
        </Text>
        <div className="grid gap-3 sm:grid-cols-2">
          {preview.values.map(({ parameter, value }) => {
            const key = propertyLinkParameterKey(parameter);
            const categories = propertyLinkCategoryOptions(parameter, t);
            const label =
              parameter.kind === PropertySharingFieldPart.kind.PROPERTY
                ? t(`propertyLinks.field.${parameter.field}`)
                : `${parameter.selector.label ?? t('propertyLinks.unlabelled')} · ${parameter.selector.system}`;
            return (
              <div className="space-y-1" key={key}>
                <Text as="label" size="tiny">
                  {label}
                </Text>
                {categories.length &&
                parameter.kind === PropertySharingFieldPart.kind.PROPERTY ? (
                  <Select
                    size="sm"
                    aria-label={`${t('propertyLinks.exampleValue')} ${label}`}
                    value={value}
                    options={categories}
                    onChange={(event) =>
                      preview.setExampleValue(parameter, event.target.value)
                    }
                  />
                ) : (
                  <TextInput
                    size="sm"
                    aria-label={`${t('propertyLinks.exampleValue')} ${label}`}
                    value={value}
                    maxLength={2048}
                    onChange={(event) =>
                      preview.setExampleValue(parameter, event.target.value)
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PropertyLinkEditorStep>
  );
}
